import {
  Metadata,
  SharedPlan,
  Plan,
  Asset,
  Environment,
  Poi,
  Isochrone,
  AudiencesQuintile,
} from '@workspaces/types'
import { SharePlanService } from '@workspaces/services'
import { sendInfoMessageToUser } from '@/helpers/message/message.helper'
import { deserializePlan } from '@/helpers/plan.helper'
import { Geometry } from '@/plan/geometries/geoboundary.type'
import { FetchLayerDataResult } from '@/services/fetch-layer/fetch-layer.type'
import { Md5 } from 'ts-md5'
import { sharePlanIsExpiracyEnabled } from '@/plan/metadata/metadata.helper'
import SharedPlanInfoManager from '@/helpers/sharedPlanInfo.helper'
import { normalizePoiIconUrlEntry } from '@/helpers/poi-icons.helper'
import { inflate } from 'pako'
export default {
  async getSharedPlanInfo(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    planId: string,
  ): Promise<SharedPlan.SharedPlanState> {
    const appVersion = environment.isTesting()
      ? meta.flavour_testing
      : meta.flavour
    const response: SharedPlan.SharedPlanState =
      await SharePlanService.getSharedPlanInfo(
        meta,
        environment,
        planId,
        appVersion,
      )
    return response
  },

  async createSharedPlan(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    planId: string,
    password: string | undefined,
    expiracyDate: Date | undefined,
  ): Promise<string> {
    const params: SharedPlan.CreateSharedPlanParameters = {
      planId,
      password: password || undefined,
      appVersion: environment.isTesting() ? meta.flavour_testing : meta.flavour,
      expiracyDate: expiracyDate?.toISOString() || undefined,
    }

    if (!sharePlanIsExpiracyEnabled(meta)) {
      params.expiracyDate = undefined
    }

    const response = await SharePlanService.createSharePlan(
      meta,
      environment,
      params,
    )
    return response
  },

  async getAssets(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    planId: string,
    password: string | undefined,
    assetsOffset?: number,
    token?: string,
    iv?: string,
  ): Promise<SharedPlan.GeoJsonAsset[] | SharedPlan.RemoteAssetResolution> {
    const params: SharedPlan.CreateSharedPlanParameters = {
      planId,
      appVersion: environment.isTesting() ? meta.flavour_testing : meta.flavour,
      password: password || undefined,
      assetsOffset: assetsOffset || undefined,
      token: token || undefined,
      iv: iv || undefined,
    }
    try {
      const response = await SharePlanService.getAssets(
        meta,
        environment,
        params,
      )
      return response
    } catch (error) {
      const errorTyped = error as Error
      sendInfoMessageToUser({
        titleLocaleKey: '',
        msgLocaleKey: '',
        title: 'Error loading plan',
        msg: errorTyped.message,
      })
      console.error(error)
      if (assetsOffset === 0) {
        return { count: 0, assets: [] }
      }
      return []
    }
  },

  async getPlan(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    planId: string,
    password: string | undefined,
  ): Promise<Plan.Plan> {
    const params: SharedPlan.CreateSharedPlanParameters = {
      planId,
      appVersion: environment.isTesting() ? meta.flavour_testing : meta.flavour,
      password: password || undefined,
    }
    const response = await SharePlanService.getPlan(meta, environment, params)
    const plan = deserializePlan(response)
    return plan
  },

  async getGeometries(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    planId: string,
    password: string | undefined,
  ): Promise<Geometry[]> {
    const params: SharedPlan.CreateSharedPlanParameters = {
      planId,
      appVersion: environment.isTesting() ? meta.flavour_testing : meta.flavour,
      password: password || undefined,
    }
    const response = await SharePlanService.getGeometries(
      meta,
      environment,
      params,
    )
    return response
  },

  async getSharedPlanPoisWithIsochrone(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    planId: string,
    password: string | undefined,
    filter: Isochrone.IsochronesFilter,
  ): Promise<Isochrone.IsochroneGeometry[]> {
    const params: SharedPlan.CreateSharedPlanParameters = {
      planId,
      appVersion: environment.isTesting() ? meta.flavour_testing : meta.flavour,
      password: password || undefined,
      isochronesFilter: filter,
    }
    const compressedData =
      await SharePlanService.getSharedPlanPoisWithIsochrone(
        meta,
        environment,
        params,
      )

    if (!compressedData) {
      return []
    }

    // Convert the Base64 string to a binary data view
    const binaryString = window.atob(compressedData)
    const len = binaryString.length
    const dataAsBuffer = new Uint8Array(len)
    for (let i = 0; i < len; i++) {
      dataAsBuffer[i] = binaryString.charCodeAt(i)
    }
    const dataDecompressed = inflate(dataAsBuffer)
    const textDecoder = new TextDecoder()
    const dataDecoded = textDecoder.decode(dataDecompressed)
    return JSON.parse(dataDecoded) as Isochrone.IsochroneGeometry[]
  },

  async getSharedActiveCustomPoisGroups(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    planId: string,
    customPoisIds: string[],
  ): Promise<Poi.CustomPOIsGroupId[]> {
    const params: SharedPlan.CreateSharedPlanParameters = {
      planId,
      customPoisIds,
      appVersion: environment.isTesting() ? meta.flavour_testing : meta.flavour,
    }
    const response = await SharePlanService.getSharedActiveCustomPoisGroups(
      meta,
      environment,
      params,
    )
    return response
  },

  async getCustomPOIs(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    planId: string,
    password: string | undefined,
    activeSubPlanFiltersIndex: number,
  ): Promise<Partial<FetchLayerDataResult>> {
    const params: SharedPlan.CreateSharedPlanParameters = {
      planId,
      appVersion: environment.isTesting() ? meta.flavour_testing : meta.flavour,
      password: password || undefined,
    }
    // The cloud function returns the FeatureCollection ready to render, with
    // the geometry parsed and `properties.id` carrying the group name, exactly
    // like the authenticated path builds it.
    const data = await SharePlanService.getCustomPOIs(
      meta,
      environment,
      params,
      activeSubPlanFiltersIndex,
    )
    return {
      format: 'geojson',
      data: data as unknown as FetchLayerDataResult['data'],
    }
  },

  async getAssetById(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    planId: string,
    password: string | undefined,
    assetId: string,
  ): Promise<Partial<Asset.AssetFull>[]> {
    const params: SharedPlan.CreateSharedPlanParameters = {
      planId,
      appVersion: environment.isTesting() ? meta.flavour_testing : meta.flavour,
      password: password || undefined,
    }
    const data: Partial<Asset.AssetFull> = await SharePlanService.getAssetById(
      meta,
      environment,
      params,
      assetId,
    )

    return [data]
  },

  async getInsights(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    planId: string,
    password: string | undefined,
  ): Promise<SharedPlan.SharedPlanInsights> {
    const params: SharedPlan.CreateSharedPlanParameters = {
      planId,
      appVersion: environment.isTesting() ? meta.flavour_testing : meta.flavour,
      password: password || undefined,
    }
    const data: SharedPlan.SharedPlanInsights =
      await SharePlanService.getInsights(meta, environment, params)
    return data
  },

  async getSharedPlanContentFromPlanId(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    planId: string,
  ): Promise<SharedPlan.SharedPlan | null> {
    const plan = await SharePlanService.getSharedPlanContentFromPlanId(
      meta,
      environment,
      planId,
    )
    return plan
  },

  async updateSharedPlanContent(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    sharedPlan: SharedPlan.SharedPlan,
  ): Promise<void> {
    const sharedPlanId = sharedPlan.id
    if (!sharedPlanId) {
      throw new Error(
        'Shared plan id is missing, and is required for update operation',
      )
    }
    if (sharedPlan.passwordRequire) {
      const password = sharedPlan.password
      if (!password) {
        throw new Error('Password is required to update shared plan')
      }
      sharedPlan.originalPassword = password
      sharedPlan.password = Md5.hashStr(password)
    } else {
      delete sharedPlan.password
      delete sharedPlan.originalPassword
    }
    await SharePlanService.updateSharedPlanContent(
      meta,
      environment,
      sharedPlanId,
      sharedPlan,
    )
  },

  async getSharedPlanAudiencesQuintiles(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    planId: string,
    password: string | undefined,
  ): Promise<AudiencesQuintile.ZipCodesByQuintile[]> {
    const params: SharedPlan.CreateSharedPlanParameters = {
      planId,
      appVersion: environment.isTesting() ? meta.flavour_testing : meta.flavour,
      password: password || undefined,
    }
    try {
      const response = await SharePlanService.getSharedPlanAudiencesQuintiles(
        meta,
        environment,
        params,
      )
      return response
    } catch (error) {
      const errorTyped = error as Error
      sendInfoMessageToUser({
        titleLocaleKey: '',
        msgLocaleKey: '',
        title: 'Error loading plan',
        msg: errorTyped.message,
      })
      console.error(error)
      return []
    }
  },

  async getSharedPlanAdvanceOptions(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    planId: string,
    password: string | undefined,
    isForExport: boolean,
  ): Promise<SharedPlan.AdvanceOptions> {
    const params: SharedPlan.CreateSharedPlanParameters = {
      planId,
      appVersion: environment.isTesting() ? meta.flavour_testing : meta.flavour,
      password: password || undefined,
    }
    const response = await SharePlanService.getSharedPlanAdvanceOptions(
      meta,
      environment,
      params,
      isForExport,
    )
    return response
  },

  /**
   * POI icon assignments for the shared plan. They are not part of the shared
   * plan payload: this endpoint refuses them unless the plan's legend has the
   * icons mode enabled, so a failure means "no icons to draw" (sc-563940).
   *
   * A cloud function deployed before the fit option returns bare URL strings;
   * those normalize to the default fit, like legacy stored assignments.
   */
  async getPoisIcons(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
  ): Promise<Record<string, Poi.PoiIconUrlEntry>> {
    const response = await this.genericCallToCloudFunction<
      Record<string, unknown>
    >(meta, environment, SharedPlan.Action.PoisIcons)

    const sources: Record<string, Poi.PoiIconUrlEntry> = {}
    for (const [selectionName, entry] of Object.entries(response ?? {})) {
      const source = normalizePoiIconUrlEntry(entry)
      if (source) {
        sources[selectionName] = source
      }
    }
    return sources
  },

  async genericCallToCloudFunction<T>(
    meta: Metadata.AppMetadata,
    environment: Environment.EnvironmentResolver,
    action: SharedPlan.Action,
    isForExport = false,
    mappingValues?: string[],
  ): Promise<T> {
    let password: string | undefined
    const planId = environment.isSharePlan().id
    const sharedPlanInfoManager = SharedPlanInfoManager.getInstance()
    const isSharedPlanPasswordRequired =
      sharedPlanInfoManager.isPasswordRequired()
    if (isSharedPlanPasswordRequired) {
      password = sharedPlanInfoManager.getPassword()
    }
    const params: SharedPlan.CreateSharedPlanParameters = {
      planId,
      appVersion: environment.isTesting() ? meta.flavour_testing : meta.flavour,
      password,
      mappingValues: mappingValues?.length ? mappingValues : undefined,
    }

    const response = await SharePlanService.genericCallToCloudFunction(
      meta,
      environment,
      params,
      action,
      isForExport,
    )
    return response as unknown as T
  },
}
